import React from 'react';
import { User } from '../types/user';
import {
  Mail,
  Phone,
  Globe,
  MapPin,
  Building,
  Briefcase,
  Linkedin,
  Github,
  Twitter,
  Facebook,
  Instagram,
  Youtube,
  MessageCircle,
  Camera,
  Paintbrush,
  PenTool,
  BookOpen,
  Hash,
  MessageSquare,
  Send,
  PhoneCall,
  QrCode,
  Share2
} from 'lucide-react';

interface PublicProfileViewProps {
  profile: User;
}

export function PublicProfileView({ profile }: PublicProfileViewProps): JSX.Element {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* En-tête avec photo et infos principales */}
        <div 
          className="relative bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-16"
          style={{
            background: profile.qrStyle?.fgColor 
              ? `linear-gradient(135deg, ${profile.qrStyle.fgColor}dd 0%, ${profile.qrStyle.fgColor}99 100%)`
              : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
          }}
        >
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="relative flex flex-col items-center">
            {profile.logoUrl && (
              <img
                src={profile.logoUrl}
                alt="Logo"
                className="w-32 h-32 rounded-full border-4 border-white shadow-xl mb-4 object-cover"
              />
            )}
            <h1 className="text-3xl font-bold text-white">
              {profile.prenom} {profile.nom}
            </h1>
            {profile.poste && (
              <div className="flex items-center mt-2 text-white/90">
                <Briefcase className="w-4 h-4 mr-2" />
                <p>{profile.poste}</p>
              </div>
            )}
            {profile.entreprise && (
              <div className="flex items-center mt-1 text-white/90">
                <Building className="w-4 h-4 mr-2" />
                <p>{profile.entreprise}</p>
              </div>
            )}
          </div>
        </div>

        <div className="p-6">
          {/* Section Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Share2 className="w-5 h-5 mr-2 text-indigo-600" />
                Contact
              </h2>
              
              {profile.email && (
                <a
                  href={`mailto:${profile.email}`}
                  className="flex items-center p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                >
                  <Mail className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 mr-3" />
                  <span className="text-gray-600 group-hover:text-gray-900">{profile.email}</span>
                </a>
              )}
              
              {profile.telephone && (
                <a
                  href={`tel:${profile.telephone}`}
                  className="flex items-center p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                >
                  <Phone className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 mr-3" />
                  <span className="text-gray-600 group-hover:text-gray-900">{profile.telephone}</span>
                </a>
              )}
              
              {profile.siteWeb && (
                <a
                  href={profile.siteWeb}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                >
                  <Globe className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 mr-3" />
                  <span className="text-gray-600 group-hover:text-gray-900">{profile.siteWeb}</span>
                </a>
              )}
            </div>

            {/* Section Adresse */}
            {(profile.adresse || profile.codePostal || profile.pays || profile.ville) && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-indigo-600" />
                  Adresse
                </h2>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    [profile.adresse, profile.ville, profile.codePostal, profile.pays]
                      .filter(Boolean)
                      .join(', ')
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-gray-400 mt-1 flex-shrink-0" />
                    <div className="ml-3 space-y-1">
                      {profile.adresse && (
                        <p className="text-gray-600">{profile.adresse}</p>
                      )}
                      {profile.ville && (
                        <p className="text-gray-600">{profile.ville}</p>
                      )}
                      {(profile.codePostal || profile.pays) && (
                        <p className="text-gray-600">
                          {profile.codePostal} {profile.pays}
                        </p>
                      )}
                    </div>
                  </div>
                </a>
              </div>
            )}
          </div>

          {/* Réseaux sociaux */}
          {(profile.linkedin || profile.github || profile.twitter || profile.facebook || 
            profile.instagram || profile.youtube || profile.tiktok || profile.behance || 
            profile.dribbble || profile.medium || profile.discord || profile.whatsapp || 
            profile.telegram || profile.skype) && (
            <div className="mt-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Share2 className="w-5 h-5 mr-2 text-indigo-600" />
                Réseaux sociaux
              </h2>
              <div className="flex flex-wrap gap-4">
                {profile.linkedin && (
                  <a
                    href={profile.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Linkedin className="w-5 h-5 text-[#0077B5] mr-2" />
                    <span className="text-gray-700">LinkedIn</span>
                  </a>
                )}
                {profile.github && (
                  <a
                    href={profile.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Github className="w-5 h-5 text-[#333] mr-2" />
                    <span className="text-gray-700">GitHub</span>
                  </a>
                )}
                {profile.twitter && (
                  <a
                    href={profile.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Twitter className="w-5 h-5 text-[#1DA1F2] mr-2" />
                    <span className="text-gray-700">Twitter</span>
                  </a>
                )}
                {profile.facebook && (
                  <a
                    href={profile.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Facebook className="w-5 h-5 text-[#1877F2] mr-2" />
                    <span className="text-gray-700">Facebook</span>
                  </a>
                )}
                {profile.instagram && (
                  <a
                    href={profile.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Instagram className="w-5 h-5 text-[#E4405F] mr-2" />
                    <span className="text-gray-700">Instagram</span>
                  </a>
                )}
                {profile.youtube && (
                  <a
                    href={profile.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Youtube className="w-5 h-5 text-[#FF0000] mr-2" />
                    <span className="text-gray-700">YouTube</span>
                  </a>
                )}
                {profile.tiktok && (
                  <a
                    href={profile.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Camera className="w-5 h-5 text-[#000000] mr-2" />
                    <span className="text-gray-700">TikTok</span>
                  </a>
                )}
                {profile.behance && (
                  <a
                    href={profile.behance}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Paintbrush className="w-5 h-5 text-[#1769FF] mr-2" />
                    <span className="text-gray-700">Behance</span>
                  </a>
                )}
                {profile.dribbble && (
                  <a
                    href={profile.dribbble}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <PenTool className="w-5 h-5 text-[#EA4C89] mr-2" />
                    <span className="text-gray-700">Dribbble</span>
                  </a>
                )}
                {profile.medium && (
                  <a
                    href={profile.medium}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <BookOpen className="w-5 h-5 text-[#000000] mr-2" />
                    <span className="text-gray-700">Medium</span>
                  </a>
                )}
                {profile.discord && (
                  <a
                    href={`discord://-/users/${profile.discord}`}
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Hash className="w-5 h-5 text-[#5865F2] mr-2" />
                    <span className="text-gray-700">Discord</span>
                  </a>
                )}
                {profile.whatsapp && (
                  <a
                    href={`https://wa.me/${profile.whatsapp.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <MessageCircle className="w-5 h-5 text-[#25D366] mr-2" />
                    <span className="text-gray-700">WhatsApp</span>
                  </a>
                )}
                {profile.telegram && (
                  <a
                    href={`https://t.me/${profile.telegram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Send className="w-5 h-5 text-[#0088CC] mr-2" />
                    <span className="text-gray-700">Telegram</span>
                  </a>
                )}
                {profile.skype && (
                  <a
                    href={`skype:${profile.skype}?chat`}
                    className="flex items-center px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <PhoneCall className="w-5 h-5 text-[#00AFF0] mr-2" />
                    <span className="text-gray-700">Skype</span>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* QR Code */}
          {profile.qrCodeUrl && (
            <div className="mt-8 flex flex-col items-center border-t pt-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <QrCode className="w-5 h-5 mr-2 text-indigo-600" />
                QR Code
              </h2>
              <img
                src={profile.qrCodeUrl}
                alt="QR Code"
                className="w-48 h-48 object-contain bg-white p-2 rounded-xl shadow-lg"
              />
              {profile.vcardUrl && (
                <a
                  href={profile.vcardUrl}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Télécharger la vCard
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
